import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import DashboardIcon from '@/components/Svg/school/menu/dashboard'
import { isCompanyCountryFrance } from '@/lib/country/entityCountries'
import { SPACE } from '@/lib/hook/spaceStore'
import { canAccessEspaceIndustry } from '@/lib/industry/canAccessEspaceIndustry'
import { INDUSTRY_ROLES, resolveIndustryRole } from '@/lib/industry/industryRoles'
import { EntityList } from '@/types/Entity'
import { CONSTANTS } from '@/types/enum/constants'
import type { IndustryNavKey } from '@/types/industry/industryTypes'

// Espace Industry (maquette Zentiva) : pages relocalisees sous /industryoffice/group/*.
// Tout l'espace est garde par le flag company `activeEspacePersonnalise` (plus de feature
// flag). Roles : reutilisation du role industry existant.

// Visible uniquement si la company a l'espace personnalise active ET si la cle de nav
// appartient au role labo resolu (siege/dr/dp).
// Le role est resolu depuis les VRAIS roles utilisateur (resolveIndustryRole), pas le
// selecteur DEMO in-page (qui ne pilote que le contenu, pas le menu).
const fnDisplayIndustryNav =
  (navKey: IndustryNavKey) =>
  ({ dataMyProfile }) =>
    canAccessEspaceIndustry(dataMyProfile) &&
    INDUSTRY_ROLES[resolveIndustryRole(dataMyProfile)].nav.includes(navKey)

// ECRANS VENDEUR (commandes de la boutique, statistiques produits, refus LGO) : ils ne sont
// PAS dans cet espace (arbitrage Mathieu 11/09/2026). Ils appartiennent a l'espace VENDEUR
// STORE (`getIndustryOfficeStoreIndustry`, /industryoffice/store/*), qui parle de la
// boutique du vendeur — pas de son activite de laboratoire. Un labo egalement vendeur les
// atteint en basculant d'espace. Ne pas les re-declarer ici : cf. la regle
// .claude/rules/space-routing-front.md.

// Ordre du menu de l'espace laboratoire (positions), arrete le 18/09/2026 : Tableau de bord (10),
// Pharmacies (20), Campagnes (25), Souhaits des pharmacies (30), Demandes de materiel (32, ajoute
// le 01/10/2026), Suivi financier (40),
// Facturation (45), Stock PLV (60), Utilisateurs & roles (70), Documents & contrats (80),
// Parametres (90). Le quotidien du labo d'abord (officines, campagnes), la finance ensuite,
// l'administration en bas.
const getIndustryOfficeGroupIndustry = [
  {
    route: 'Dashboard',
    text: 'Tableau de bord',
    t: 'industry.nav.dashboard',
    icon: DashboardIcon,
    href: '/industryoffice/group/home',
    home: true,
    menuHeader: true,
    position: 1,
    space: SPACE.PHARMEDIGROUP,
    // Un vendeur a son propre « Tableau de bord » ; la page reste ouverte, sans seconde entrée.
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) =>
      !(
        dataMyProfile?.selectCompanyDefault?.companyIdStore &&
        dataMyProfile?.loggedUserRoles?.includes(CONSTANTS.ROLE_DYNAMIC_ROUTE_INDUSTRY_USER_STORE_VENDOR)
      ),
  },
  {
    route: 'MyProfile',
    text: 'Mon profil',
    t: 'industry.nav.myProfile',
    href: '/backoffice/common/my-profile',
    menuHeader: false,
  },
  // --- Espace Industry (gating feature flag + activeEspacePersonnalise) ---
  {
    route: 'IndustryDashboard',
    text: 'Tableau de bord',
    t: 'industry.nav.dashboard',
    icon: DashboardIcon,
    href: '/industryoffice/group/dashboard',
    menuHeader: true,
    position: 10,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('dashboard'),
  },
  {
    // Suivi financier (Siege/DR uniquement — pas Delegue). Gate front par le role labo
    // reel (fnDisplayIndustryNav('finance')) ET par l'API (ROLE_INDUSTRY_DR, fail-closed).
    route: 'IndustryFinance',
    text: 'Suivi financier',
    t: 'industry.nav.finance',
    icon: PaymentsOutlinedIcon,
    href: '/industryoffice/group/finance',
    menuHeader: true,
    position: 40,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('finance'),
  },
  {
    // Facturation (Siege/DR uniquement, comme le suivi financier) : liste des factures
    // signees des programmes du labo, gate front par le role labo reel ET par l'API
    // (ROLE_INDUSTRY_DR, fail-closed via /industry/invoices).
    route: 'IndustryInvoices',
    text: 'Facturation',
    t: 'industry.nav.invoices',
    icon: RequestQuoteOutlinedIcon,
    href: '/industryoffice/group/facturation',
    menuHeader: true,
    position: 45,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('invoices'),
  },
  {
    route: 'IndustryPharmacies',
    text: 'Pharmacies',
    t: 'industry.nav.pharmacies',
    icon: LocalPharmacyOutlinedIcon,
    href: '/industryoffice/group/pharmacies',
    menuHeader: true,
    position: 20,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('pharmacies'),
  },
  {
    // Souhaits produits exprimes par les pharmacies (dont la radio, produit virtuel du
    // catalogue). Siege/DR comme le suivi financier : l'API refuse le Delegue
    // (ROLE_INDUSTRY_DR, fail-closed) et applique en plus le perimetre territorial.
    route: 'IndustryPlvWishes',
    text: 'Souhaits des pharmacies',
    t: 'industry.nav.wishes',
    icon: FavoriteBorderOutlinedIcon,
    href: '/industryoffice/group/souhaits-pharmacies',
    menuHeader: true,
    position: 30,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('wishes'),
  },
  {
    // DEMANDES DE MATERIEL PLV (W7, workflow cible du 01/10/2026) : le Siege valide ou refuse
    // lui-meme les demandes de SES pharmacies (materiel manquant, abime, a renouveler). L'API
    // impose le perimetre (`/industry/plv_replacement_requests`, decision refusee hors du labo)
    // et n'ouvre la decision qu'a ROLE_INDUSTRY_USER_ADMINISTRATOR : d'ou la cle de nav du
    // seul Siege. France seule : la PLV n'est commercialisee qu'en France (ENTITY_COUNTRIES).
    route: 'IndustryPlvRequests',
    text: 'Demandes de matériel',
    t: 'industry.nav.plvRequests',
    icon: FactCheckOutlinedIcon,
    href: '/industryoffice/group/demandes-materiel',
    menuHeader: true,
    position: 32,
    space: SPACE.INDUSTRY,
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayIndustryNav('plvRequests')({ dataMyProfile }) &&
      isCompanyCountryFrance(dataMyProfile?.selectCompanyDefault),
  },
  {
    route: 'IndustryUsersRoles',
    text: 'Utilisateurs & rôles',
    t: 'industry.nav.usersRoles',
    icon: GroupsOutlinedIcon,
    href: '/industryoffice/group/users-roles',
    menuHeader: true,
    position: 70,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('users'),
  },
  {
    // Reutilise le module Zones existant (pages/industryoffice/group/zone-list).
    // HORS MENU depuis le 18/09/2026 : les zones se reglent une fois, elles s'ouvrent depuis
    // la page Parametres (carte « Zones »). La route reste declaree — sans elle withAuthent
    // renverrait a l'accueil — et garde sa condition d'acces 'zones'. Tout role qui voit les
    // zones voit aussi Parametres (cf. INDUSTRY_ROLES) : personne ne perd l'acces.
    route: 'IndustryZones',
    text: 'Zones',
    t: 'industry.nav.zones',
    icon: MapOutlinedIcon,
    href: '/industryoffice/group/zone-list',
    menuHeader: false,
    position: 50,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('zones'),
  },
  {
    route: 'IndustryStockPlv',
    text: 'Stock PLV',
    t: 'industry.nav.stockPlv',
    icon: Inventory2OutlinedIcon,
    href: '/industryoffice/group/stock-plv',
    menuHeader: true,
    position: 60,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('stock'),
  },
  {
    route: 'IndustryCampagnes',
    text: 'Campagnes',
    t: 'industry.nav.campagnes',
    icon: TrendingUpOutlinedIcon,
    href: '/industryoffice/group/campagnes',
    menuHeader: true,
    position: 25,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('campagnes'),
  },
  {
    route: 'IndustryDocuments',
    text: 'Documents & contrats',
    t: 'industry.nav.documents',
    icon: DescriptionOutlinedIcon,
    href: '/industryoffice/group/documents',
    menuHeader: true,
    position: 80,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('docs'),
  },
  {
    route: 'IndustrySettings',
    text: 'Paramètres',
    t: 'industry.nav.settings',
    icon: SettingsOutlinedIcon,
    href: '/industryoffice/group/settings',
    menuHeader: true,
    position: 90,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('settings'),
  },
  // Guide de l'espace laboratoire (hors menu, 28/09/2026) : icone « ? » du tableau de bord (tous
  // les roles) et, pour le Siege, Parametres. Cle de nav 'guide' (Siege, DR, delegue) et France
  // seulement — le document est en francais et l'espace n'est ouvert qu'en France. Le pays est lu sur la
  // company du compte (`countries` est vide pour un compte hors back-office).
  {
    route: 'IndustryGuide',
    text: "Guide de l'espace",
    t: 'industry.nav.guide',
    href: '/industryoffice/group/guide',
    menuHeader: false,
    space: SPACE.INDUSTRY,
    fnDisplay: ({ dataMyProfile }) =>
      fnDisplayIndustryNav('guide')({ dataMyProfile }) &&
      isCompanyCountryFrance(dataMyProfile?.selectCompanyDefault),
  },
  // Edition de zone (hors menu) — variante Espace Industry de `ZoneEdit` : reutilise la
  // meme page (pages/industryoffice/group/zone/edit/[zoneId]) mais garde l'utilisateur
  // dans l'espace Industry (route taggee SPACE.INDUSTRY). Rattachee a la nav 'zones'.
  {
    route: 'IndustryZoneEdit',
    text: 'Édition de zone',
    t: 'industry.nav.zoneEdit',
    href: '/industryoffice/group/zone/edit/[zoneId]',
    menuHeader: false,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('zones'),
  },
  // Fiche pharmacie 360 (hors menu) — declaree pour l'autorisation de route.
  // Rattachee a la nav 'pharmacies' (visible si le role peut voir les pharmacies).
  {
    route: 'IndustryPharmacyDetail',
    text: 'Fiche pharmacie',
    t: 'industry.nav.pharmacyDetail',
    href: '/industryoffice/group/pharmacy/[id]',
    menuHeader: false,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('pharmacies'),
  },
  // Suivi d'UNE campagne partenaire (hors menu) — variante labo de `PartnerCampaignTracking` :
  // meme ecran, memes colonnes, lignes scopees fail-closed cote serveur. `[campaignId]` = id de
  // la CAMPAGNE (et non d'un programme). Rattachee a la nav 'campagnes'.
  {
    route: 'IndustryPartnerCampaignTracking',
    text: 'Suivi de la campagne',
    t: 'industry.nav.campaignTracking',
    href: '/industryoffice/group/partner-campaign/[campaignId]',
    menuHeader: false,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('campagnes'),
  },
  // Detail campagne en consultation (hors menu). `[id]` = programId ; `?module=Sound|PLV`
  // distingue Sound/StoreBag. Rattachee a la nav 'campagnes'.
  {
    route: 'IndustryCampaignDetail',
    text: 'Détail campagne',
    t: 'industry.nav.campaignDetail',
    href: '/industryoffice/group/campagne/[id]',
    menuHeader: false,
    space: SPACE.INDUSTRY,
    fnDisplay: fnDisplayIndustryNav('campagnes'),
  },
].map(route => ({
  ...route,
  entity: [EntityList.PHARMEDIGROUP],
  roles: [CONSTANTS.ROLE_DYNAMIC_ROUTE_INDUSTRY_USER_ENTITY_GROUP],
}))

export default getIndustryOfficeGroupIndustry
